'use client';

import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  BodyLinkV3,
  ButtonV3,
  GridItem,
  InverseHeader,
  LayoutGrid,
  TypographyV3,
} from '@jouzen/ecom-components';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import ArtDirectionImage from '@/app/components/Image/ArtDirectionImage';
import { Section } from '@/app/components/LegacyLandingPage/components';

interface OverlineProps {
  readonly newsAgency: string;
  readonly date: MessageKey;
}

const Overline = ({ newsAgency, date }: OverlineProps) => {
  const t = useTranslations();
  return (
    <TypographyV3 className="text-sm">
      {newsAgency} • <span className="font-light text-gray-400">{t(date)}</span>
    </TypographyV3>
  );
};

const HeaderContainer = ({ children }: { readonly children: ReactNode }) => (
  <div className="max-w-[80vw]">{children}</div>
);

const tValue = {
  a: function a(chunks: ReactNode) {
    return (
      <BodyLinkV3
        href="mailto:press@ouraring.com"
        color="blue-200"
        className="text-blue-200"
      >
        {chunks}
      </BodyLinkV3>
    );
  },
};

const Content = (): JSX.Element => {
  const t = useTranslations();

  return (
    <>
      <section data-cy="press-hero-section">
        <InverseHeader enter>
          <LayoutGrid>
            <GridItem
              className="relative"
              colEnd={{ sm: 'full' }}
              colStart={{ sm: 'full' }}
              rowEnd={{ sm: 2 }}
              rowStart={{ sm: 1 }}
            >
              <ArtDirectionImage
                alt={t('press_hero_image_alt_text')}
                className="object-cover"
                fill
                format="png"
                sizes="(max-width: 767px) 100vw, 25vw"
                srcSet={[
                  {
                    src: 'press/press-hero-mobile',
                    media: '(max-width: 767px)',
                  },
                  {
                    src: 'press/press-hero-desktop2x',
                    media: '(min-width: 768px)',
                  },
                ]}
              />
            </GridItem>
            <GridItem
              className="relative py-64 lg:py-60"
              colEnd={{ sm: 'main' }}
              colStart={{ sm: 'main' }}
              rowEnd={{ sm: 2 }}
              rowStart={{ sm: 1 }}
            >
              <TypographyV3
                Element="h1"
                variant="h4"
                color="light"
                className="mb-4"
              >
                {t('press_title')}
              </TypographyV3>
              <TypographyV3 variant="h1" color="light">
                {t('press_title_subheading')}
              </TypographyV3>
            </GridItem>
          </LayoutGrid>
        </InverseHeader>
      </section>

      <Section className="relative -mt-8 rounded-3xl border-t bg-sandstone-100 pt-16">
        <LayoutGrid>
          <GridItem
            colEnd={{ sm: 'main', md: 20, lg: 19 }}
            colStart={{ sm: 'main', md: 5, lg: 7 }}
            className="mb-24 lg:mx-8 xl:mx-20"
          >
            <TypographyV3 variant="h6" weight="normal">
              {t.rich('press_content_heading', tValue)}
            </TypographyV3>
          </GridItem>
        </LayoutGrid>
        <LayoutGrid>
          <TypographyV3
            className="col-main lg:col-end-8"
            variant="h4"
            Element="h2"
          >
            {t('press_list_title')}
          </TypographyV3>

          <GridItem colEnd={{ sm: 'main' }} colStart={{ sm: 'main', lg: 9 }}>
            <Accordion openAtIndex={1}>
              <AccordionHeader>
                <HeaderContainer>
                  <Overline newsAgency="CNET" date="press_cnet_publish_date" />
                  <TypographyV3 variant="h6" Element="h3" weight="normal">
                    {t.rich('press_item_1_title_2023')}
                  </TypographyV3>
                </HeaderContainer>
              </AccordionHeader>
              <AccordionContent>
                <TypographyV3 className="mb-5 mt-0">
                  {t('press_item_1_body_2023')}
                </TypographyV3>
                <ButtonV3
                  href="https://www.cnet.com/tech/mobile/oura-ring-can-now-tell-if-youre-a-morning-person/"
                  variant="secondary-dark"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('press_read_article')}
                </ButtonV3>
              </AccordionContent>

              <AccordionHeader>
                <HeaderContainer>
                  <Overline
                    newsAgency="Esquire"
                    date="press_esquire_publish_date"
                  />
                  <TypographyV3 variant="h6" Element="h3" weight="normal">
                    {t.rich('press_item_2_title_2023')}
                  </TypographyV3>
                </HeaderContainer>
              </AccordionHeader>
              <AccordionContent>
                <TypographyV3 className="mb-5 mt-0">
                  {t('press_item_2_body_2023')}
                </TypographyV3>
                <ButtonV3
                  href="https://www.esquire.com/lifestyle/a42354480/oura-ring-review/"
                  variant="secondary-dark"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('press_read_article')}
                </ButtonV3>
              </AccordionContent>

              <AccordionHeader>
                <HeaderContainer>
                  <Overline
                    newsAgency="Good Housekeeping"
                    date="press_good_housekeeping_publish_date"
                  />
                  <TypographyV3 variant="h6" Element="h3" weight="normal">
                    {t.rich('press_item_3_title_2023')}
                  </TypographyV3>
                </HeaderContainer>
              </AccordionHeader>
              <AccordionContent>
                <TypographyV3 className="mb-5 mt-0">
                  {t('press_item_3_body_2023')}
                </TypographyV3>
                <ButtonV3
                  href="https://www.goodhousekeeping.com/health-products/a42015954/2023-fitness-awards/"
                  variant="secondary-dark"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('press_read_article')}
                </ButtonV3>
              </AccordionContent>

              <AccordionHeader>
                <HeaderContainer>
                  <Overline
                    newsAgency="Fierce Healthcare"
                    date="press_fierce_publish_date"
                  />
                  <TypographyV3 variant="h6" Element="h3" weight="normal">
                    {t.rich('press_item_4_title_2023')}
                  </TypographyV3>
                </HeaderContainer>
              </AccordionHeader>
              <AccordionContent>
                <TypographyV3 className="mb-5 mt-0">
                  {t('press_item_4_body_2023')}
                </TypographyV3>
                <ButtonV3
                  href="https://www.fiercehealthcare.com/digital-health/oura-business-has-already-worked-200-orgs-throughout-commerce-provide-insight"
                  variant="secondary-dark"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('press_read_article')}
                </ButtonV3>
              </AccordionContent>

              <AccordionHeader>
                <HeaderContainer>
                  <Overline
                    newsAgency="Fast Company"
                    date="press_fast_co_publish_date"
                  />
                  <TypographyV3 variant="h6" Element="h3" weight="normal">
                    {t.rich('press_item_5_title_2023')}
                  </TypographyV3>
                </HeaderContainer>
              </AccordionHeader>
              <AccordionContent>
                <TypographyV3 className="mb-5 mt-0">
                  {t('press_item_5_body_2023')}
                </TypographyV3>
                <ButtonV3
                  href="https://www.fastcompany.com/90804436/health-wellness-and-fitness-the-10-next-big-things-from-surgical-ar-to-a-better-bike-computer"
                  variant="secondary-dark"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('press_read_article')}
                </ButtonV3>
              </AccordionContent>

              <AccordionHeader>
                <HeaderContainer>
                  <Overline
                    newsAgency="The New York Times"
                    date="press_ny_times_publish_date"
                  />
                  <TypographyV3 variant="h6" Element="h3" weight="normal">
                    {t.rich('press_item_6_title_2023')}
                  </TypographyV3>
                </HeaderContainer>
              </AccordionHeader>
              <AccordionContent>
                <TypographyV3 className="mb-5 mt-0">
                  {t('press_item_6_body_2023')}
                </TypographyV3>
                <ButtonV3
                  href="https://www.nytimes.com/2022/08/29/fashion/jewelry-gucci-ring-health-technology.html"
                  variant="secondary-dark"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('press_read_article')}
                </ButtonV3>
              </AccordionContent>

              <AccordionHeader>
                <HeaderContainer>
                  <Overline
                    newsAgency="The Verge"
                    date="press_verge_publish_date"
                  />
                  <TypographyV3 variant="h6" Element="h3" weight="normal">
                    {t.rich('press_item_7_title_2023')}
                  </TypographyV3>
                </HeaderContainer>
              </AccordionHeader>
              <AccordionContent>
                <TypographyV3 className="mb-5 mt-0">
                  {t('press_item_7_body_2023')}
                </TypographyV3>
                <ButtonV3
                  href="https://www.theverge.com/2022/8/2/23287187/natural-cycles-digital-birth-control-oura-temperature"
                  variant="secondary-dark"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('press_read_article')}
                </ButtonV3>
              </AccordionContent>

              <AccordionHeader>
                <HeaderContainer>
                  <Overline newsAgency="CNBC" date="press_cnbc_publish_date" />
                  <TypographyV3 variant="h6" Element="h3" weight="normal">
                    {t.rich('press_item_8_title_2023')}
                  </TypographyV3>
                </HeaderContainer>
              </AccordionHeader>
              <AccordionContent>
                <TypographyV3 className="mb-5 mt-0">
                  {t('press_item_8_body_2023')}
                </TypographyV3>
                <ButtonV3
                  href="https://www.cnbc.com/2022/05/17/these-are-the-2022-cnbc-disruptor-50-companies.html"
                  variant="secondary-dark"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('press_read_article')}
                </ButtonV3>
              </AccordionContent>

              <AccordionHeader>
                <HeaderContainer>
                  <Overline
                    newsAgency="Tech Crunch"
                    date="press_tech_crunch_publish_date"
                  />
                  <TypographyV3 variant="h6" Element="h3" weight="normal">
                    {t.rich('press_item_9_title_2023')}
                  </TypographyV3>
                </HeaderContainer>
              </AccordionHeader>
              <AccordionContent>
                <TypographyV3 className="mb-5 mt-0">
                  {t('press_item_9_body_2023')}
                </TypographyV3>
                <ButtonV3
                  href="https://techcrunch.com/2022/03/24/oura-sells-its-millionth-ring/"
                  variant="secondary-dark"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('press_read_article')}
                </ButtonV3>
              </AccordionContent>

              <AccordionHeader>
                <HeaderContainer>
                  <Overline
                    newsAgency="Vogue"
                    date="press_vogue_publish_date"
                  />
                  <TypographyV3 variant="h6" Element="h3" weight="normal">
                    {t.rich('press_item_10_title_2023')}
                  </TypographyV3>
                </HeaderContainer>
              </AccordionHeader>
              <AccordionContent>
                <TypographyV3 className="mb-5 mt-0">
                  {t('press_item_10_body_2023')}
                </TypographyV3>
                <ButtonV3
                  href="https://www.vogue.com/article/this-ring-is-changing-how-i-track-my-period"
                  variant="secondary-dark"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('press_read_article')}
                </ButtonV3>
              </AccordionContent>
            </Accordion>
          </GridItem>
        </LayoutGrid>
      </Section>
    </>
  );
};

export default Content;
